import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X, AlertCircle, AlertTriangle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@shared/services/api';
import { normalizeBackupType } from '../utils/backupType';

interface BackupConfig {
  id: string;
  name: string;
  description: string | null;
  type: string;
  agent_id: string | null;
  storage_destination_id: string | null;
  is_enabled: boolean;
}

interface BackupError {
  type: 'error' | 'warning';
  title: string;
  message: string;
}

interface BackupNowModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BackupNowModal({ isOpen, onClose }: BackupNowModalProps) {
  const { t } = useTranslation('backups');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [isTriggering, setIsTriggering] = useState(false);
  const [backupError, setBackupError] = useState<BackupError | null>(null);

  // Fetch backup configurations
  const { data: configs, isLoading } = useQuery({
    queryKey: ['backup-configs'],
    queryFn: async () => {
      const response = await api.get('/backups/configs');
      return response.data as BackupConfig[];
    },
    enabled: isOpen,
  });

  // Trigger backup mutation
  const triggerBackup = useMutation({
    mutationFn: async (configId: string) => {
      const response = await api.post('/backup-jobs', {
        backup_config_id: configId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setIsTriggering(false);
      setBackupError(null);
      onClose();
      // Redirect to backup jobs page
      navigate('/backup-jobs');
    },
    onError: (err: any) => {
      setIsTriggering(false);
      const responseData = err?.response?.data;
      const apiError =
        typeof responseData === 'string'
          ? responseData.trim()
          : typeof responseData?.error === 'string'
            ? responseData.error
            : '';
      const errorCode = typeof responseData === 'object' ? responseData?.code : undefined;

      if (errorCode === 'SUBSCRIPTION_REQUIRED') {
        setBackupError({
          type: 'warning',
          title: t('backupNow.errors.subscriptionRequired', 'Assinatura Necessária'),
          message: [
            apiError,
            t(
              'backupNow.errors.subscribePrompt',
              'Faça um upgrade do seu plano para executar backups sob demanda.'
            ),
          ]
            .filter(Boolean)
            .join('\n\n'),
        });
      } else if (errorCode === 'AGENT_OFFLINE') {
        setBackupError({
          type: 'warning',
          title: t('backupNow.errors.agentOffline', 'Agente Offline'),
          message:
            apiError ||
            t(
              'backupNow.errors.agentOfflineMessage',
              'O agente está offline. O backup será executado quando o agente ficar online.'
            ),
        });
      } else if (errorCode === 'CONFIG_DISABLED') {
        setBackupError({
          type: 'error',
          title: t('backupNow.errors.configDisabled', 'Configuração Desabilitada'),
          message:
            apiError ||
            t(
              'backupNow.errors.configDisabledMessage',
              'Esta configuração de backup está desabilitada.'
            ),
        });
      } else {
        setBackupError({
          type: 'error',
          title: t('backupNow.errors.triggerFailed', 'Falha ao Iniciar Backup'),
          message:
            apiError ||
            t(
              'backupNow.errors.unknownError',
              'Ocorreu um erro ao iniciar o backup. Tente novamente.'
            ),
        });
      }
    },
  });

  const handleTriggerBackup = () => {
    if (!selectedConfigId) return;
    setIsTriggering(true);
    setBackupError(null);
    triggerBackup.mutate(selectedConfigId);
  };

  const handleClose = () => {
    setBackupError(null);
    setSelectedConfigId('');
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;

    openerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusFrame = window.requestAnimationFrame(() => {
      const initialFocus = dialogRef.current?.querySelector<HTMLElement>(
        '[data-dialog-autofocus="true"], button:not([disabled]), input:not([disabled])'
      );
      initialFocus?.focus();
    });

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousBodyOverflow;
      openerRef.current?.focus();
      openerRef.current = null;
    };
  }, [isOpen]);

  const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && !isTriggering) {
      event.preventDefault();
      handleClose();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (focusable.length === 0) {
      event.preventDefault();
      dialogRef.current?.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  const enabledConfigs = configs?.filter((c) => c.is_enabled) || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="backup-now-dialog-title"
      tabIndex={-1}
      ref={dialogRef}
      onKeyDown={handleDialogKeyDown}
    >
      <div className="my-auto max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-lg bg-surface-card-light shadow-xl dark:bg-surface-2-dark">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-surface-border-light dark:border-surface-border-dark">
          <h2
            id="backup-now-dialog-title"
            className="text-xl font-semibold text-slate-900 dark:text-white"
          >
            {t('backupNow.title')}
          </h2>
          <button
            type="button"
            aria-label={tCommon('actions.close')}
            onClick={handleClose}
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-text-subtle-light transition-colors hover:text-slate-700 dark:text-text-subtle-dark dark:hover:text-slate-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Error/Warning Message */}
          {backupError && (
            <div
              className={`mb-4 p-4 rounded-lg border ${
                backupError.type === 'error'
                  ? 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20'
                  : 'bg-state-warn-bg-light dark:bg-state-warn-bg-dark border-state-warn-light/20 dark:border-state-warn-dark/20'
              }`}
            >
              <div className="flex items-start gap-3">
                {backupError.type === 'error' ? (
                  <AlertCircle className="w-5 h-5 text-state-danger-light dark:text-state-danger-dark flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-state-warn-light dark:text-state-warn-dark flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <h4
                    className={`text-sm font-semibold ${
                      backupError.type === 'error'
                        ? 'text-state-danger-light dark:text-state-danger-dark'
                        : 'text-state-warn-light dark:text-state-warn-dark'
                    }`}
                  >
                    {backupError.title}
                  </h4>
                  <p
                    className={`text-sm mt-1 whitespace-pre-line ${
                      backupError.type === 'error'
                        ? 'text-state-danger-light dark:text-state-danger-dark'
                        : 'text-state-warn-light dark:text-state-warn-dark'
                    }`}
                  >
                    {backupError.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-state-info-light dark:border-state-info-dark"></div>
              <p className="mt-2 text-text-muted-light dark:text-text-muted-dark">
                {t('backupNow.loading')}
              </p>
            </div>
          ) : enabledConfigs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-text-muted-light dark:text-text-muted-dark">
                {t('backupNow.noConfigs.title')}
              </p>
              <p className="text-sm text-text-muted-light dark:text-text-muted-dark mt-2">
                {t('backupNow.noConfigs.message')}
              </p>
            </div>
          ) : (
            <div>
              <p className="text-sm text-text-muted-light dark:text-text-muted-dark mb-4">
                {t('backupNow.selectPrompt')}
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {enabledConfigs.map((config) => (
                  <label
                    key={config.id}
                    className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedConfigId === config.id
                        ? 'border-state-info-light dark:border-state-info-dark bg-state-info-bg-light dark:bg-state-info-bg-dark'
                        : 'border-surface-border-light dark:border-border-strong-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                    }`}
                  >
                    <input
                      type="radio"
                      name="backup-config"
                      value={config.id}
                      checked={selectedConfigId === config.id}
                      onChange={(e) => setSelectedConfigId(e.target.value)}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-slate-900 dark:text-white">
                        {config.name}
                      </div>
                      {config.description && (
                        <div className="text-sm text-text-muted-light dark:text-text-muted-dark mt-1">
                          {config.description}
                        </div>
                      )}
                      <div className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                        {t('backupNow.typeLabel')}:{' '}
                        {normalizeBackupType(config.type) === 'FILES'
                          ? t('filters.type.files')
                          : normalizeBackupType(config.type) === 'POSTGRESQL'
                            ? t('filters.type.postgresql')
                            : config.type}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-surface-border-light dark:border-surface-border-dark bg-surface-2-light dark:bg-surface-2-dark rounded-b-lg">
          <button
            type="button"
            onClick={handleClose}
            disabled={isTriggering}
            className="min-h-11 rounded-lg border border-border-strong-light bg-surface-card-light px-4 text-slate-700 transition-colors hover:bg-surface-2-light disabled:cursor-not-allowed disabled:opacity-50 dark:border-border-strong-dark dark:bg-surface-border-dark dark:text-slate-300 dark:hover:bg-surface-border-dark"
          >
            {t('backupNow.buttons.cancel')}
          </button>
          <button
            type="button"
            onClick={handleTriggerBackup}
            disabled={!selectedConfigId || isTriggering}
            className="min-h-11 rounded-lg bg-brand-600 px-4 text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isTriggering ? (
              <span className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('backupNow.buttons.triggering')}
              </span>
            ) : (
              t('backupNow.buttons.start')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
